import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  getManagersFromYandex,
  getManagerFromYandex
} from '../../../api/yandexUserApi';
import {
  type IManagersData,
  type IManagerFromYandex
} from '../../../types/IManagersFromYandex';
import {
  CANCEL_CODE,
  getErrorData,
  type IErrorData
} from '../../../utils/getErrorData';
import { isError } from '../../../utils/typeguards/isError';
import { hasResponse } from '@/utils/typeguards/hasResponse';

export interface IGetManagersThunkOptions {
  signal: AbortSignal;
}

/**
 * Thunk для получения списка менеджеров
 */
export const getManagersThunk = createAsyncThunk<
  IManagersData,
  IGetManagersThunkOptions,
  { rejectValue: IErrorData | undefined }
>('managers/getManagers', async ({ signal }, { rejectWithValue }) => {
  try {
    const data = await getManagersFromYandex({ signal });

    if (isError(data)) {
      return rejectWithValue(data);
    }

    if (!hasResponse<IManagersData>(data)) {
      return rejectWithValue({ message: 'Некорректный ответ сервера' });
    }

    return data.response;
  } catch (error) {
    const extractedError = getErrorData(error);

    if (extractedError.code === CANCEL_CODE) {
      return rejectWithValue(undefined);
    }

    return rejectWithValue(extractedError);
  }
});

export interface IGetManagerThunkOptions {
  userId: string;
  signal: AbortSignal;
}

/**
 * Thunk для получения одного менеджера
 */
export const getManagerThunk = createAsyncThunk<
  IManagerFromYandex,
  IGetManagerThunkOptions,
  { rejectValue: IErrorData | undefined }
>('managers/getManager', async ({ userId, signal }, { rejectWithValue }) => {
  try {
    const data = await getManagerFromYandex(userId, { signal });

    if (isError(data)) {
      return rejectWithValue(data);
    }

    if (!hasResponse<IManagerFromYandex>(data)) {
      return rejectWithValue({ message: 'Некорректный ответ сервера' });
    }

    return data.response;
  } catch (error) {
    const extractedError = getErrorData(error);

    if (extractedError.code === CANCEL_CODE) {
      return rejectWithValue(undefined);
    }

    return rejectWithValue(extractedError);
  }
});
